import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Phone, Mail, MapPin, Home, Key, Building2, Search, Instagram, Facebook, MessageCircle, ChevronRight, Bed, Maximize, Building, Car, Waves, ArrowRight } from 'lucide-react';

const HomePage = () => {
  const { language, changeLanguage } = useLanguage();
  const [activeTab, setActiveTab] = useState('all');
  const [showEstimationForm, setShowEstimationForm] = useState(false);
  
  // Traductions complètes BAYITFULL
  const t = {
    fr: {
      nav: { home: 'ACCUEIL', properties: 'BIENS', newProjects: 'PROJETS NEUFS', neighborhoods: 'QUARTIERS', about: 'VANESSA', contact: 'CONTACT' },
      hero: { 
        tagline: "L'immobilier à Netanya, autrement.",
        services: "Vente • Location • Projets neufs • Biens d'exception",
        locations: "Netanya • Ir Yamim • Nat 600 • Front de mer",
        cta1: "DÉCOUVRIR NOS BIENS",
        cta2: "WHATSAPP VANESSA"
      },
      search: { title: 'Nos', highlight: 'Biens', all: 'Tous', sale: 'Vente', rent: 'Location', newProject: 'Projets neufs' },
      neighborhoods: { 
        title: 'Découvrez nos', highlight: 'secteurs',
        irYamim: { name: 'IR YAMIM', desc: 'Immobilier haut de gamme à quelques pas de la mer.' },
        nat600: { name: 'NAT 600', desc: 'Nouveaux projets, résidences modernes et opportunités d\'investissement.' },
        frontMer: { name: 'FRONT DE MER', desc: 'Appartements, penthouses et propriétés avec vue Méditerranée.' },
        netanya: { name: 'NETANYA', desc: 'Vente et location dans les quartiers les plus recherchés.' }
      },
      vanessa: {
        title: 'Vanessa Benaim Kakun',
        subtitle: 'Votre interlocutrice immobilière à Netanya',
        desc: 'Acheter, vendre ou louer un bien en Israël est avant tout un projet personnel. Chez Bayitfull, je vous accompagne à chaque étape avec une connaissance approfondie du marché de Netanya et une attention particulière portée à vos besoins.',
        cta: 'Parler avec Vanessa sur WhatsApp',
        languages: 'Français • עברית • English • Русский'
      },
      owners: {
        title: 'VOUS AVEZ UN BIEN À VENDRE OU À LOUER ?',
        subtitle: 'Confiez votre propriété à Bayitfull.',
        cta1: 'ESTIMER MON BIEN',
        cta2: 'CONTACTER VANESSA',
        form: { name: 'Votre nom', phone: 'Téléphone', type: 'Type de bien', neighborhood: 'Quartier', purpose: 'Vente ou Location', submit: 'Envoyer ma demande' }
      },
      property: { 
        rooms: 'pièces', sqm: 'm²', floor: 'étage', terrace: 'Terrasse', parking: 'Parking', seaView: 'Vue mer', elevator: 'Ascenseur',
        available: 'Disponible', ref: 'Réf',
        ctaWhatsapp: 'WHATSAPP VANESSA',
        ctaVisit: 'DEMANDER UNE VISITE',
        noProperties: 'Les biens seront bientôt disponibles. Contactez Vanessa pour être informé en priorité.'
      },
      footer: { 
        tagline: 'More than a home. It\'s your Bayit.',
        taglineHe: 'בית מושלם, חיים יפים',
        rights: '© 2025 BAYITFULL. Tous droits réservés.'
      },
      whatsapp: {
        defaultMsg: 'Bonjour Vanessa, je vous contacte depuis Bayitfull. Je recherche un bien immobilier en Israël.',
        propertyMsg: 'Bonjour Vanessa, je suis intéressé(e) par le bien [REF] vu sur Bayitfull. Pouvez-vous me donner plus d\'informations ?'
      },
      mobileBar: { whatsapp: 'WhatsApp', call: 'Appeler', properties: 'Biens' }
    },
    en: {
      nav: { home: 'HOME', properties: 'PROPERTIES', newProjects: 'NEW PROJECTS', neighborhoods: 'AREAS', about: 'VANESSA', contact: 'CONTACT' },
      hero: { 
        tagline: "Real estate in Netanya, redefined.",
        services: "Sale • Rental • New projects • Exceptional properties",
        locations: "Netanya • Ir Yamim • Nat 600 • Seafront",
        cta1: "DISCOVER OUR PROPERTIES",
        cta2: "WHATSAPP VANESSA"
      },
      search: { title: 'Our', highlight: 'Properties', all: 'All', sale: 'Sale', rent: 'Rent', newProject: 'New projects' },
      neighborhoods: { 
        title: 'Discover our', highlight: 'areas',
        irYamim: { name: 'IR YAMIM', desc: 'High-end real estate just steps from the sea.' },
        nat600: { name: 'NAT 600', desc: 'New projects, modern residences and investment opportunities.' },
        frontMer: { name: 'SEAFRONT', desc: 'Apartments, penthouses and properties with Mediterranean views.' },
        netanya: { name: 'NETANYA', desc: 'Sales and rentals in the most sought-after neighborhoods.' }
      },
      vanessa: {
        title: 'Vanessa Benaim Kakun',
        subtitle: 'Your real estate contact in Netanya',
        desc: 'Buying, selling or renting property in Israel is above all a personal project. At Bayitfull, I accompany you every step of the way with an in-depth knowledge of the Netanya market and particular attention to your needs.',
        cta: 'Chat with Vanessa on WhatsApp',
        languages: 'Français • עברית • English • Русский'
      },
      owners: {
        title: 'DO YOU HAVE A PROPERTY TO SELL OR RENT?',
        subtitle: 'Entrust your property to Bayitfull.',
        cta1: 'ESTIMATE MY PROPERTY',
        cta2: 'CONTACT VANESSA',
        form: { name: 'Your name', phone: 'Phone', type: 'Property type', neighborhood: 'Neighborhood', purpose: 'Sale or Rental', submit: 'Send my request' }
      },
      property: { 
        rooms: 'rooms', sqm: 'sqm', floor: 'floor', terrace: 'Terrace', parking: 'Parking', seaView: 'Sea view', elevator: 'Elevator',
        available: 'Available', ref: 'Ref',
        ctaWhatsapp: 'WHATSAPP VANESSA',
        ctaVisit: 'REQUEST A VISIT',
        noProperties: 'Properties will be available soon. Contact Vanessa to be informed first.'
      },
      footer: { 
        tagline: 'More than a home. It\'s your Bayit.',
        taglineHe: 'בית מושלם, חיים יפים',
        rights: '© 2025 BAYITFULL. All rights reserved.'
      },
      whatsapp: {
        defaultMsg: 'Hello Vanessa, I am contacting you from Bayitfull. I am looking for a property in Israel.',
        propertyMsg: 'Hello Vanessa, I am interested in property [REF] seen on Bayitfull. Can you give me more information?'
      },
      mobileBar: { whatsapp: 'WhatsApp', call: 'Call', properties: 'Properties' }
    },
    he: {
      nav: { home: 'בית', properties: 'נכסים', newProjects: 'פרויקטים חדשים', neighborhoods: 'שכונות', about: 'ונסה', contact: 'צור קשר' },
      hero: { 
        tagline: "נדל\"ן בנתניה, אחרת.",
        services: "מכירה • השכרה • פרויקטים חדשים • נכסי יוקרה",
        locations: "נתניה • עיר ימים • נת 600 • חזית הים",
        cta1: "גלה את הנכסים שלנו",
        cta2: "וואטסאפ ונסה"
      },
      search: { title: '', highlight: 'הנכסים שלנו', all: 'הכל', sale: 'מכירה', rent: 'השכרה', newProject: 'פרויקטים חדשים' },
      neighborhoods: { 
        title: 'גלו את', highlight: 'האזורים שלנו',
        irYamim: { name: 'עיר ימים', desc: 'נדל"ן יוקרתי במרחק צעדים מהים.' },
        nat600: { name: 'נת 600', desc: 'פרויקטים חדשים, מגורים מודרניים והזדמנויות השקעה.' },
        frontMer: { name: 'חזית הים', desc: 'דירות, פנטהאוזים ונכסים עם נוף לים התיכון.' },
        netanya: { name: 'נתניה', desc: 'מכירה והשכרה בשכונות המבוקשות ביותר.' }
      },
      vanessa: {
        title: 'ונסה בנאים קקון',
        subtitle: 'איש הקשר שלך לנדל"ן בנתניה',
        desc: 'קנייה, מכירה או השכרה של נכס בישראל הוא קודם כל פרויקט אישי. ב-Bayitfull, אני מלווה אותך בכל שלב עם ידע מעמיק של שוק נתניה ותשומת לב מיוחדת לצרכים שלך.',
        cta: 'דברו עם ונסה בוואטסאפ',
        languages: 'Français • עברית • English • Русский'
      },
      owners: {
        title: 'יש לכם נכס למכירה או להשכרה?',
        subtitle: 'הפקידו את הנכס שלכם ב-Bayitfull.',
        cta1: 'הערכת הנכס שלי',
        cta2: 'צרו קשר עם ונסה',
        form: { name: 'שם', phone: 'טלפון', type: 'סוג נכס', neighborhood: 'שכונה', purpose: 'מכירה או השכרה', submit: 'שלח בקשה' }
      },
      property: { 
        rooms: 'חדרים', sqm: 'מ"ר', floor: 'קומה', terrace: 'מרפסת', parking: 'חניה', seaView: 'נוף לים', elevator: 'מעלית',
        available: 'זמין', ref: 'מס׳',
        ctaWhatsapp: 'וואטסאפ ונסה',
        ctaVisit: 'בקשת ביקור',
        noProperties: 'הנכסים יהיו זמינים בקרוב. צרו קשר עם ונסה כדי להיות הראשונים לדעת.'
      },
      footer: { 
        tagline: 'More than a home. It\'s your Bayit.',
        taglineHe: 'בית מושלם, חיים יפים',
        rights: '© 2025 BAYITFULL. כל הזכויות שמורות.'
      },
      whatsapp: {
        defaultMsg: 'שלום ונסה, אני פונה אליך מ-Bayitfull. אני מחפש/ת נכס בישראל.',
        propertyMsg: 'שלום ונסה, אני מעוניין/ת בנכס [REF] שראיתי ב-Bayitfull. אפשר לקבל מידע נוסף?'
      },
      mobileBar: { whatsapp: 'וואטסאפ', call: 'התקשר', properties: 'נכסים' }
    },
    ru: {
      nav: { home: 'ГЛАВНАЯ', properties: 'НЕДВИЖИМОСТЬ', newProjects: 'НОВОСТРОЙКИ', neighborhoods: 'РАЙОНЫ', about: 'ВАНЕССА', contact: 'КОНТАКТЫ' },
      hero: { 
        tagline: "Недвижимость в Нетании по-новому.",
        services: "Продажа • Аренда • Новостройки • Элитная недвижимость",
        locations: "Нетания • Ир Ямим • Нат 600 • Набережная",
        cta1: "СМОТРЕТЬ ОБЪЕКТЫ",
        cta2: "WHATSAPP ВАНЕССЕ"
      },
      search: { title: 'Наша', highlight: 'Недвижимость', all: 'Все', sale: 'Продажа', rent: 'Аренда', newProject: 'Новостройки' },
      neighborhoods: { 
        title: 'Откройте наши', highlight: 'районы',
        irYamim: { name: 'ИР ЯМИМ', desc: 'Элитная недвижимость в нескольких шагах от моря.' },
        nat600: { name: 'НАТ 600', desc: 'Новые проекты, современные резиденции и инвестиционные возможности.' },
        frontMer: { name: 'НАБЕРЕЖНАЯ', desc: 'Квартиры, пентхаусы и недвижимость с видом на Средиземное море.' },
        netanya: { name: 'НЕТАНИЯ', desc: 'Продажа и аренда в самых востребованных районах.' }
      },
      vanessa: {
        title: 'Ванесса Бенаим Какун',
        subtitle: 'Ваш контакт по недвижимости в Нетании',
        desc: 'Покупка, продажа или аренда недвижимости в Израиле — это прежде всего личный проект. В Bayitfull я сопровождаю вас на каждом этапе с глубоким знанием рынка Нетании и особым вниманием к вашим потребностям.',
        cta: 'Написать Ванессе в WhatsApp',
        languages: 'Français • עברית • English • Русский'
      },
      owners: {
        title: 'У ВАС ЕСТЬ НЕДВИЖИМОСТЬ НА ПРОДАЖУ ИЛИ В АРЕНДУ?',
        subtitle: 'Доверьте вашу недвижимость Bayitfull.',
        cta1: 'ОЦЕНИТЬ МОЮ НЕДВИЖИМОСТЬ',
        cta2: 'СВЯЗАТЬСЯ С ВАНЕССОЙ',
        form: { name: 'Ваше имя', phone: 'Телефон', type: 'Тип недвижимости', neighborhood: 'Район', purpose: 'Продажа или Аренда', submit: 'Отправить запрос' }
      },
      property: { 
        rooms: 'комн.', sqm: 'м²', floor: 'этаж', terrace: 'Терраса', parking: 'Парковка', seaView: 'Вид на море', elevator: 'Лифт',
        available: 'Доступно', ref: 'Арт.',
        ctaWhatsapp: 'WHATSAPP ВАНЕССЕ',
        ctaVisit: 'ЗАПРОСИТЬ ПРОСМОТР',
        noProperties: 'Объекты скоро появятся. Свяжитесь с Ванессой, чтобы узнать первыми.'
      },
      footer: { 
        tagline: 'More than a home. It\'s your Bayit.',
        taglineHe: 'בית מושלם, חיים יפים',
        rights: '© 2025 BAYITFULL. Все права защищены.'
      },
      whatsapp: {
        defaultMsg: 'Здравствуйте Ванесса, я обращаюсь к вам с Bayitfull. Ищу недвижимость в Израиле.',
        propertyMsg: 'Здравствуйте Ванесса, меня интересует объект [REF] на Bayitfull. Можете дать больше информации?'
      },
      mobileBar: { whatsapp: 'WhatsApp', call: 'Позвонить', properties: 'Объекты' }
    }
  };

  const txt = t[language] || t.fr;
  const isRTL = language === 'he';
  
  // Photos
  const PHOTO_HERO = "https://images.pexels.com/photos/11176923/pexels-photo-11176923.jpeg?auto=compress&cs=tinysrgb&w=1920";
  const PHOTO_VANESSA = "https://customer-assets.emergentagent.com/job_netanya-elite-homes/artifacts/53bi7cuj_IMG_0239.png";
  const LOGO_BAYITFULL = "https://customer-assets-7cd3h4nn.emergentagent.net/job_816c0d52-aa0d-4edc-bb15-bb6a0034980a/artifacts/cak45w6k_ChatGPT%20Image%2016%20aou%CC%82t%202026%20a%CC%80%2021_08_19.png";
  
  // Images quartiers
  const NEIGHBORHOOD_IMAGES = {
    irYamim: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800&q=80",
    nat600: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&q=80",
    frontMer: "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800&q=80",
    netanya: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=80"
  };

  // WhatsApp
  const WHATSAPP_NUMBER = "972523570058";
  const openWhatsApp = (message) => {
    const encodedMsg = encodeURIComponent(message || txt.whatsapp.defaultMsg);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMsg}`, '_blank');
  };

  // Couleurs design
  const colors = {
    violet: "#6b21a8", // violet-800 - plus sombre et élégant
    violetLight: "#7c3aed",
    anthracite: "#1f2937",
    stone: "#78716c",
    offWhite: "#fafaf9"
  };

  // Properties (vide pour l'instant - sera rempli par l'admin)
  const properties = [];

  return (
    <div className={`min-h-screen bg-[#fafaf9] text-gray-900 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/98 backdrop-blur-md border-b border-gray-100">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <span className="text-2xl font-semibold tracking-tight">
              <span className="text-[#6b21a8]">BAYIT</span><span className="text-gray-800">FULL</span>
            </span>
          </div>
          <div className="hidden lg:flex items-center space-x-8 text-sm tracking-wide text-gray-600">
            <a href="#home" className="hover:text-[#6b21a8] transition-colors font-medium">{txt.nav.home}</a>
            <a href="#properties" className="hover:text-[#6b21a8] transition-colors font-medium">{txt.nav.properties}</a>
            <a href="#new-projects" className="hover:text-[#6b21a8] transition-colors font-medium">{txt.nav.newProjects}</a>
            <a href="#neighborhoods" className="hover:text-[#6b21a8] transition-colors font-medium">{txt.nav.neighborhoods}</a>
            <a href="#vanessa" className="hover:text-[#6b21a8] transition-colors font-medium">{txt.nav.about}</a>
            <a href="#contact" className="hover:text-[#6b21a8] transition-colors font-medium">{txt.nav.contact}</a>
          </div>
          <div className="flex items-center space-x-2">
            {['FR', 'EN', 'HE', 'RU'].map(lang => (
              <button
                key={lang}
                onClick={() => changeLanguage(lang.toLowerCase())}
                className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                  language === lang.toLowerCase() 
                    ? 'bg-[#6b21a8] text-white' 
                    : 'text-gray-500 hover:text-[#6b21a8]'
                }`}
              >
                {lang}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center pt-16">
        <div className="absolute inset-0">
          <img 
            src={PHOTO_HERO} 
            alt="Netanya Seafront" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
        </div>
        
        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          {/* Logo */}
          <div className="mb-8">
            <img 
              src={LOGO_BAYITFULL} 
              alt="BAYITFULL" 
              className="h-32 md:h-44 lg:h-52 mx-auto object-contain drop-shadow-2xl"
            />
          </div>
          
          {/* Tagline */}
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-light text-white mb-6 tracking-wide">
            {txt.hero.tagline}
          </h1>
          
          {/* Services */}
          <p className="text-sm md:text-base text-white/80 mb-3 tracking-widest uppercase">
            {txt.hero.services}
          </p>
          
          {/* Locations */}
          <p className="text-sm md:text-base text-white/70 mb-12 tracking-wider">
            {txt.hero.locations}
          </p>
          
          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              className="bg-white text-[#6b21a8] hover:bg-gray-100 px-8 py-6 text-sm tracking-widest font-semibold min-w-[250px] transition-all duration-300"
              onClick={() => document.getElementById('properties').scrollIntoView({ behavior: 'smooth' })}
            >
              {txt.hero.cta1}
            </Button>
            <Button 
              className="bg-[#25D366] hover:bg-[#128C7E] text-white px-8 py-6 text-sm tracking-widest font-semibold min-w-[250px] transition-all duration-300 flex items-center justify-center gap-2"
              onClick={() => openWhatsApp()}
            >
              <MessageCircle className="w-5 h-5" />
              {txt.hero.cta2}
            </Button>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronRight className="w-6 h-6 text-white/50 rotate-90" />
        </div>
      </section>

      {/* Neighborhoods Section */}
      <section id="neighborhoods" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-light text-center mb-16">
            <span className="text-gray-400">{txt.neighborhoods.title}</span>{' '}
            <span className="text-[#6b21a8] font-medium">{txt.neighborhoods.highlight}</span>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { key: 'irYamim', img: NEIGHBORHOOD_IMAGES.irYamim },
              { key: 'nat600', img: NEIGHBORHOOD_IMAGES.nat600 },
              { key: 'frontMer', img: NEIGHBORHOOD_IMAGES.frontMer },
              { key: 'netanya', img: NEIGHBORHOOD_IMAGES.netanya }
            ].map((area) => (
              <div 
                key={area.key}
                className="group relative h-80 rounded-lg overflow-hidden cursor-pointer"
              >
                <img 
                  src={area.img} 
                  alt={txt.neighborhoods[area.key].name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-white text-xl font-semibold mb-2 tracking-wide">
                    {txt.neighborhoods[area.key].name}
                  </h3>
                  <p className="text-white/80 text-sm leading-relaxed">
                    {txt.neighborhoods[area.key].desc}
                  </p>
                  <div className="mt-4 flex items-center text-white/60 text-sm group-hover:text-white transition-colors">
                    <span>Voir les biens</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Properties Section */}
      <section id="properties" className="py-24 bg-[#fafaf9]">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-light text-center mb-8">
            <span className="text-gray-400">{txt.search.title}</span>{' '}
            <span className="text-[#6b21a8] font-medium">{txt.search.highlight}</span>
          </h2>
          
          {/* Tabs */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-white rounded-lg p-1 shadow-sm border border-gray-100">
              {[
                { key: 'all', label: txt.search.all },
                { key: 'sale', label: txt.search.sale },
                { key: 'rent', label: txt.search.rent },
                { key: 'new', label: txt.search.newProject }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-6 py-3 text-sm font-medium rounded-md transition-all ${
                    activeTab === tab.key
                      ? 'bg-[#6b21a8] text-white'
                      : 'text-gray-600 hover:text-[#6b21a8]'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Properties Grid or Empty State */}
          {properties.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Property cards will go here */}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
              <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-6" />
              <p className="text-gray-500 text-lg mb-8 max-w-md mx-auto">
                {txt.property.noProperties}
              </p>
              <Button 
                className="bg-[#25D366] hover:bg-[#128C7E] text-white px-8 py-4"
                onClick={() => openWhatsApp()}
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                {txt.hero.cta2}
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* New Projects Section */}
      <section id="new-projects" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-light text-center mb-8">
            <span className="text-gray-400">{txt.nav.newProjects}</span>
          </h2>
          
          <div className="text-center py-16 bg-[#fafaf9] rounded-2xl border border-gray-100">
            <Building className="w-16 h-16 text-gray-300 mx-auto mb-6" />
            <p className="text-gray-500 text-lg mb-8 max-w-md mx-auto">
              {language === 'fr' ? 'Les projets neufs seront bientôt disponibles.' : 
               language === 'he' ? 'פרויקטים חדשים יהיו זמינים בקרוב.' :
               language === 'ru' ? 'Новые проекты скоро появятся.' :
               'New projects will be available soon.'}
            </p>
            <Button 
              className="bg-[#25D366] hover:bg-[#128C7E] text-white px-8 py-4"
              onClick={() => openWhatsApp()}
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              {txt.hero.cta2}
            </Button>
          </div>
        </div>
      </section>

      {/* Vanessa Section */}
      <section id="vanessa" className="py-24 bg-[#fafaf9]">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Photo */}
              <div className="relative">
                <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src={PHOTO_VANESSA} 
                    alt="Vanessa Benaim Kakun"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-[#6b21a8]/10 rounded-full -z-10"></div>
              </div>
              
              {/* Content */}
              <div className={isRTL ? 'text-right' : 'text-left'}>
                <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-2">
                  {txt.vanessa.title}
                </h2>
                <p className="text-[#6b21a8] text-lg mb-6">
                  {txt.vanessa.subtitle}
                </p>
                <p className="text-gray-600 text-lg leading-relaxed mb-8">
                  {txt.vanessa.desc}
                </p>
                
                <Button 
                  className="bg-[#25D366] hover:bg-[#128C7E] text-white px-8 py-4 text-base mb-4"
                  onClick={() => openWhatsApp()}
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  {txt.vanessa.cta}
                </Button>
                
                <p className="text-gray-500 text-sm">
                  {txt.vanessa.languages}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Owners Section */}
      <section id="owners" className="py-24 bg-[#1f2937]">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-light text-white mb-4">
              {txt.owners.title}
            </h2>
            <p className="text-xl text-gray-400 mb-12">
              {txt.owners.subtitle}
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                className="bg-white text-[#1f2937] hover:bg-gray-100 px-8 py-6 text-sm tracking-widest font-semibold min-w-[220px]"
                onClick={() => setShowEstimationForm(true)}
              >
                {txt.owners.cta1}
              </Button>
              <Button 
                className="bg-[#25D366] hover:bg-[#128C7E] text-white px-8 py-6 text-sm tracking-widest font-semibold min-w-[220px]"
                onClick={() => openWhatsApp()}
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                {txt.owners.cta2}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Estimation Form Modal */}
      {showEstimationForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full relative">
            <button 
              onClick={() => setShowEstimationForm(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
            <h3 className="text-2xl font-light text-gray-900 mb-6">{txt.owners.cta1}</h3>
            <form className="space-y-4">
              <input 
                type="text" 
                placeholder={txt.owners.form.name}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#6b21a8]"
              />
              <input 
                type="tel" 
                placeholder={txt.owners.form.phone}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#6b21a8]"
              />
              <select className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#6b21a8] text-gray-600">
                <option>{txt.owners.form.type}</option>
                <option>Appartement</option>
                <option>Penthouse</option>
                <option>Villa</option>
              </select>
              <select className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#6b21a8] text-gray-600">
                <option>{txt.owners.form.neighborhood}</option>
                <option>Ir Yamim</option>
                <option>Nat 600</option>
                <option>Front de mer</option>
                <option>Netanya Centre</option>
              </select>
              <select className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#6b21a8] text-gray-600">
                <option>{txt.owners.form.purpose}</option>
                <option>{language === 'fr' ? 'Vente' : language === 'he' ? 'מכירה' : language === 'ru' ? 'Продажа' : 'Sale'}</option>
                <option>{language === 'fr' ? 'Location' : language === 'he' ? 'השכרה' : language === 'ru' ? 'Аренда' : 'Rental'}</option>
              </select>
              <Button 
                type="submit"
                className="w-full bg-[#6b21a8] hover:bg-[#581c87] text-white py-4"
              >
                {txt.owners.form.submit}
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-12">
              {txt.nav.contact}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="p-6">
                <Phone className="w-8 h-8 text-[#6b21a8] mx-auto mb-4" />
                <p className="text-gray-600">052 357 0058</p>
              </div>
              <div className="p-6">
                <Mail className="w-8 h-8 text-[#6b21a8] mx-auto mb-4" />
                <p className="text-gray-600">vanessa.kakun@gmail.com</p>
              </div>
              <div className="p-6">
                <MapPin className="w-8 h-8 text-[#6b21a8] mx-auto mb-4" />
                <p className="text-gray-600">Netanya, Israel</p>
              </div>
            </div>
            
            <Button 
              className="bg-[#25D366] hover:bg-[#128C7E] text-white px-12 py-6 text-lg"
              onClick={() => openWhatsApp()}
            >
              <MessageCircle className="w-6 h-6 mr-3" />
              {txt.hero.cta2}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1f2937] py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-3xl font-semibold tracking-tight">
              <span className="text-[#a78bfa]">BAYIT</span><span className="text-white">FULL</span>
            </span>
            <p className="text-gray-400 mt-4 italic">{txt.footer.tagline}</p>
            <p className="text-gray-500 mt-2 text-sm">{txt.footer.taglineHe}</p>
          </div>
          
          <div className="flex justify-center space-x-6 mb-12">
            <a href="https://instagram.com/bayitfull.realestate" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#6b21a8] transition-colors">
              <Instagram className="w-5 h-5 text-white" />
            </a>
            <a href="https://facebook.com/bayitfull.realestate" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#6b21a8] transition-colors">
              <Facebook className="w-5 h-5 text-white" />
            </a>
          </div>
          
          <div className="border-t border-white/10 pt-8 text-center">
            <p className="text-gray-500 text-sm">{txt.footer.rights}</p>
            <p className="text-gray-600 text-sm mt-2">www.bayitfull.com</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button - Desktop */}
      <div className="hidden md:block fixed bottom-8 right-8 z-40">
        <button
          onClick={() => openWhatsApp()}
          className="bg-[#25D366] hover:bg-[#128C7E] text-white w-16 h-16 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110"
          aria-label="WhatsApp"
        >
          <MessageCircle className="w-8 h-8" />
        </button>
      </div>

      {/* Mobile Bottom Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 safe-area-inset-bottom">
        <div className="grid grid-cols-3 h-16">
          <button
            onClick={() => openWhatsApp()}
            className="flex flex-col items-center justify-center text-[#25D366]"
          >
            <MessageCircle className="w-6 h-6" />
            <span className="text-xs mt-1">{txt.mobileBar.whatsapp}</span>
          </button>
          <a
            href="tel:+972523570058"
            className="flex flex-col items-center justify-center text-gray-600"
          >
            <Phone className="w-6 h-6" />
            <span className="text-xs mt-1">{txt.mobileBar.call}</span>
          </a>
          <button
            onClick={() => document.getElementById('properties').scrollIntoView({ behavior: 'smooth' })}
            className="flex flex-col items-center justify-center text-gray-600"
          >
            <Home className="w-6 h-6" />
            <span className="text-xs mt-1">{txt.mobileBar.properties}</span>
          </button>
        </div>
      </div>

      {/* Padding for mobile bottom bar */}
      <div className="md:hidden h-16"></div>

    </div>
  );
};

export default HomePage;
